import React, { useEffect, useRef, useState } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Icon, IconChoices } from '../../common/Icon'
import { Button, ButtonVariant } from '../../common/Button'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Comment } from '../Comment'
import { CommentInput } from '../CommentInput'
import { PostImages } from '../PostImages'
import { PostDocuments } from '../PostDocuments'
import { PostEvents } from '../PostEvents'
import { PostPolls } from '../PostPolls'
import { useModal } from '../../../hooks/useModal'
import { PeopleModal } from '../PeopleModal'
import { Spinner } from '../../common/Spinner'
import { theme } from '../../../theme/theme'
import './post.css'
import { PostLocalOffers } from './PostLocalOffers'
import { PostResources } from './PostResources'
import { PostContacts } from './PostContacts'
import { PostFacilities } from './PostFacilities'
import { PostHeader } from './PostHeader'
import { PostText } from './PostText'
import { toggleLike } from '../../../api/posts'
import { commentQueries } from '../../../api/comments'
import { isParentDenied, isRefusedRequest } from '../../../api/helpers'
import { peopleQueryKey } from '../../../api/people'
import { PostRow } from '../../../generated/api/post-row'
import {
  cancelPostRows,
  likedRow,
  refreshPostRow,
  resumePostRows,
  updatePostRow,
  withdrawPost,
} from '../postRowCache'

interface PostProps {
  row: PostRow
  displayComments?: boolean
  targetCommentId?: string
}

export const Post = ({
  row,
  displayComments = false,
  targetCommentId,
}: PostProps): React.ReactNode => {
  const [commentSelected, setCommentSelected] =
    useState<boolean>(displayComments)
  const [anchor, setAnchor] = useState<string | undefined>(targetCommentId)
  const { translate } = useAppTranslation()
  const anchorRef = useRef<HTMLDivElement>(null)
  const scrolledTo = useRef<string | undefined>(undefined)
  const queryClient = useQueryClient()
  const {
    show: showLikesModal,
    open: openLikesModal,
    close: closeLikesModal,
  } = useModal()
  const {
    show: showSeenByModal,
    open: openSeenByModal,
    close: closeSeenByModal,
  } = useModal()

  const discussion = useInfiniteQuery({
    ...commentQueries.discussion(row.id, anchor),
    enabled: commentSelected,
  })

  useEffect(() => {
    if (targetCommentId) {
      setAnchor(targetCommentId)
    }
  }, [targetCommentId])

  const loaded = !!discussion.data && !discussion.isPlaceholderData
  useEffect(() => {
    if (anchor && anchor !== scrolledTo.current && anchorRef.current) {
      scrolledTo.current = anchor
      anchorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      })
    }
  }, [anchor, loaded])

  const { mutate: toggleLikeMutation, isPending: isLiking } = useMutation({
    mutationFn: ({ like }: { like: boolean }) => toggleLike(row.id, like),
    meta: { skipGlobalInvalidate: true },
    onMutate: async ({ like }) => {
      const interrupted = await cancelPostRows(queryClient)
      updatePostRow(queryClient, row.id, (_row) => likedRow(_row, like))
      return { interrupted }
    },
    onError: (_error, { like }) => {
      updatePostRow(queryClient, row.id, (_row) => likedRow(_row, !like))
    },
    onSettled: (_data, _error, _variables, context) => {
      queryClient.invalidateQueries({
        queryKey: peopleQueryKey({ kind: 'post-likers', id: row.id }),
      })
      refreshPostRow(queryClient, row.id)
      resumePostRows(queryClient, context?.interrupted)
    },
  })

  const { actions, society } = row
  const societyLockedForViewer = !actions.canLike

  const onCommentsClick = (): void => setCommentSelected(!commentSelected)

  const onToggleLike = (): void => {
    toggleLikeMutation({ like: !row.likes.viewerLiked })
  }

  const onLikesClick = (): void => {
    row.likes.count > 0 && openLikesModal()
  }

  const onCommentDeleted = (commentId: string): void => {
    if (commentId === anchor) {
      setAnchor(undefined)
    }
  }

  const commentCount = row.comments.count

  const commentButtonLabel = (): string => {
    if (commentCount === 0) {
      return translate('common.makeComment')
    }
    return `${commentCount} ${translate(
      commentCount === 1 ? 'common.comment' : 'common.comments'
    )}`
  }

  const showSeenBy = row.reads.visible && row.reads.count > 0

  const parentDenied =
    discussion.isError && isParentDenied(discussion.error, anchor)
  const anchorUnavailable =
    !!anchor &&
    !parentDenied &&
    discussion.isError &&
    isRefusedRequest(discussion.error)
  const withdrawn = anchorUnavailable || parentDenied
  const transientFailure = discussion.isError && !withdrawn && !loaded

  useEffect(() => {
    if (parentDenied) {
      withdrawPost(queryClient, row.id)
    }
  }, [parentDenied, queryClient, row.id])
  const surface = withdrawn ? undefined : discussion.data?.pages[0]
  const comments = withdrawn
    ? []
    : discussion.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div
      className={`my-4 flex flex-col gap-4 rounded-md border bg-white p-4 md:p-6 ${
        row.isPinned ? 'border-brandGreen' : 'border-neutral-80'
      }`}
    >
      <PostHeader row={row} />
      <div className="post-body mt-0 flex max-w-full flex-col gap-4">
        <PostText
          isAutomaticallyCreatedPost={row.generatedMessage}
          message={row.message}
        />
        <PostImages media={row.media} createdAt={row.createdAt} />
        <PostDocuments documents={row.documents} />
        <PostEvents events={row.events} />
        <PostPolls postId={row.id} polls={row.polls} />
        <PostLocalOffers offers={row.offers} societyId={society.id} />
        <PostResources resources={row.resources} />
        <PostContacts contacts={row.contacts} />
        <PostFacilities facilities={row.facilities} />
      </div>
      <div className="border-b border-neutral-80" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex">
          <div className="mr-3 flex w-32 rounded border border-neutral-80">
            <button
              className={`flex flex-1 items-center justify-center gap-1.5
                border-neutral-80 p-3
                hover:bg-neutral-96
                ${
                  row.likes.count ? 'rounded-tl rounded-bl border-r' : 'rounded'
                }
                ${societyLockedForViewer && 'cursor-not-allowed opacity-50'}
                `}
              disabled={societyLockedForViewer || isLiking}
              onClick={onToggleLike}
            >
              <Icon
                icon={IconChoices.LIKE}
                highlighted={row.likes.viewerLiked}
                size={18}
              />
              <p style={theme.textVariants.base}>{translate('common.like')}</p>
            </button>
            {!!row.likes.count && (
              <button
                onClick={onLikesClick}
                className="flex flex-1 items-center justify-center
              rounded-tr rounded-br p-3 hover:bg-neutral-96"
              >
                {row.likes.count}
              </button>
            )}
          </div>
          <Button
            className={`${commentSelected ? 'bg-neutral-96' : ''}`}
            label={commentButtonLabel()}
            icon={IconChoices.COMMENT}
            onClick={onCommentsClick}
            disabled={!actions.canComment && commentCount === 0}
          />
        </div>
        {showSeenBy && (
          <div>
            <button
              className="mt-2 flex flex-1 items-center gap-1 md:mt-0"
              onClick={openSeenByModal}
            >
              <Icon icon={IconChoices.OPEN_EYE} size={18} />
              <p style={theme.textVariants.caption} className="text-neutral-30">
                {translate('post.seenBy')} {row.reads.count}
              </p>
            </button>
          </div>
        )}
      </div>
      {commentSelected && (
        <div className="mt-2 flex flex-col gap-4">
          {discussion.isPending && (
            <div className="flex justify-center">
              <Spinner />
            </div>
          )}
          {anchorUnavailable && (
            <div className="flex flex-col items-start gap-2">
              <p style={theme.textVariants.base}>
                {translate('post.commentUnavailable')}
              </p>
              <Button
                className="flex w-fit"
                variant={ButtonVariant.TEXT}
                label={translate('post.showDiscussion')}
                onClick={() => setAnchor(undefined)}
              />
            </div>
          )}
          {withdrawn && !anchorUnavailable && (
            <p style={theme.textVariants.base}>
              {translate('flashMessage.somethingWentWrongError')}
            </p>
          )}
          {transientFailure && (
            <div className="flex flex-col items-start gap-2">
              <p style={theme.textVariants.base}>
                {translate('flashMessage.somethingWentWrongError')}
              </p>
              <Button
                className="flex w-fit"
                variant={ButtonVariant.TEXT}
                label={translate('common.actions.tryAgain')}
                onClick={() => discussion.refetch()}
              />
            </div>
          )}
          {surface && discussion.hasPreviousPage && (
            <Button
              className="flex w-fit"
              onClick={() => discussion.fetchPreviousPage()}
              loading={discussion.isFetchingPreviousPage}
              variant={ButtonVariant.TEXT}
              label={translate('post.showEarlierComments')}
            />
          )}
          {surface &&
            comments.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                society={surface.society}
                onDeleted={onCommentDeleted}
                innerRef={comment.id === anchor ? anchorRef : undefined}
              />
            ))}
          {surface && discussion.hasNextPage && (
            <Button
              className="flex w-fit"
              onClick={() => discussion.fetchNextPage()}
              loading={discussion.isFetchingNextPage}
              variant={ButtonVariant.TEXT}
              label={translate('post.showMoreComments')}
            />
          )}
          {surface?.composition.canComment && (
            <CommentInput
              postId={row.id}
              society={surface.society}
              composition={surface.composition}
              onCreated={setAnchor}
            />
          )}
        </div>
      )}
      {showLikesModal && (
        <PeopleModal
          icon={IconChoices.HEART}
          iconHighlighted
          title={`${row.likes.count} ${translate('common.likes').toLowerCase()}`}
          source={{ kind: 'post-likers', id: row.id }}
          close={closeLikesModal}
        />
      )}
      {showSeenByModal && (
        <PeopleModal
          icon={IconChoices.OPEN_EYE}
          title={`${translate('post.seenBy')} ${row.reads.count}`}
          source={{ kind: 'post-readers', id: row.id }}
          close={closeSeenByModal}
        />
      )}
    </div>
  )
}
